import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { orgUiPath } from '@/core/api';
import { useOrgId } from '@/hooks/useOrgId';
import { formatRelativeDate } from '@/utils/date';
import { downloadRenderContentFile } from '@/services/renders';
import type { RenderJobSummary } from '@/types/api';

function formatDurationMs(ms: number | null): string {
  if (ms == null || ms < 0) return '—';
  if (ms < 1000) return `${ms} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)} s`;
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return `${m}m ${r}s`;
}

function statusClass(status: RenderJobSummary['status']): string {
  switch (status) {
    case 'completed':
      return 'aspectly:text-success aspectly:font-bold';
    case 'failed':
      return 'aspectly:text-error aspectly:font-bold';
    case 'rendering':
      return 'aspectly:text-primary aspectly:font-bold';
    default:
      return 'aspectly:text-on-surface-variant';
  }
}

export interface RenderJobListProps {
  rows: RenderJobSummary[];
  /** When true, show a Creative column linking to the editor. */
  showCreativeColumn?: boolean;
  /** Highlight and scroll to a render row (deep link). */
  highlightRenderId?: number | null;
  /** How render id cells link into the router. */
  renderLinkMode?: 'org' | 'creative' | 'none';
  emptyMessage?: string;
}

export function RenderJobList({
  rows,
  showCreativeColumn = false,
  highlightRenderId = null,
  renderLinkMode = 'org',
  emptyMessage = 'No renders yet.',
}: RenderJobListProps) {
  const orgId = useOrgId();
  const highlightRef = useRef<HTMLTableRowElement | null>(null);

  useEffect(() => {
    if (highlightRenderId == null) return;
    highlightRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [highlightRenderId, rows.length]);

  if (rows.length === 0) {
    return (
      <div className="aspectly:flex aspectly:flex-col aspectly:items-center aspectly:justify-center aspectly:py-12 aspectly:text-center aspectly:bg-surface-container-lowest aspectly:rounded-2xl aspectly:border aspectly:border-outline-variant/20">
        <span className="material-symbols-outlined aspectly:text-4xl aspectly:text-on-surface-variant/30 aspectly:mb-3">
          movie
        </span>
        <p className="aspectly:text-sm aspectly:font-medium aspectly:text-on-surface-variant">{emptyMessage}</p>
      </div>
    );
  }

  function renderDeepLink(r: RenderJobSummary): string | null {
    if (renderLinkMode === 'none') return null;
    if (renderLinkMode === 'creative') {
      return orgUiPath(orgId, `/creatives/${r.creative_id}/renders/${r.id}`);
    }
    return orgUiPath(orgId, `/renders/${r.id}`);
  }

  return (
    <div className="aspectly:overflow-x-auto aspectly:border aspectly:border-outline-variant/20 aspectly:rounded-2xl aspectly:bg-surface-container-lowest">
      <table className="aspectly:w-full aspectly:text-left aspectly:text-sm aspectly:whitespace-nowrap">
        <thead>
          <tr className="aspectly:border-b aspectly:border-outline-variant/10 aspectly:text-on-surface-variant aspectly:text-xs aspectly:uppercase aspectly:tracking-wider aspectly:bg-surface-container-low/30">
            {showCreativeColumn && <th className="aspectly:px-5 aspectly:py-3 aspectly:font-bold">Creative</th>}
            <th className="aspectly:px-5 aspectly:py-3 aspectly:font-bold">Render</th>
            <th className="aspectly:px-5 aspectly:py-3 aspectly:font-bold">Status</th>
            <th className="aspectly:px-5 aspectly:py-3 aspectly:font-bold">Resolution</th>
            <th className="aspectly:px-5 aspectly:py-3 aspectly:font-bold">Completed</th>
            <th className="aspectly:px-5 aspectly:py-3 aspectly:font-bold">Render Time</th>
            <th className="aspectly:px-5 aspectly:py-3 aspectly:font-bold aspectly:text-right aspectly:border-l aspectly:border-outline-variant/5">
              Output
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const isHighlighted = highlightRenderId != null && r.id === highlightRenderId;
            const deepLink = renderDeepLink(r);
            return (
              <tr
                key={r.id}
                ref={isHighlighted ? highlightRef : undefined}
                className={`aspectly:border-b aspectly:border-outline-variant/10 aspectly:last:border-0 aspectly:hover:bg-surface-container-low/50 aspectly:transition-colors ${
                  isHighlighted
                    ? 'aspectly:bg-primary-container/40 aspectly:ring-2 aspectly:ring-inset aspectly:ring-primary/30'
                    : ''
                }`}
              >
                {showCreativeColumn && (
                  <td className="aspectly:px-5 aspectly:py-3">
                    <Link
                      to={orgUiPath(orgId, `/creatives/${r.creative_id}`)}
                      className="aspectly:text-primary aspectly:hover:text-primary-dim aspectly:hover:underline aspectly:font-bold aspectly:transition-colors"
                    >
                      {r.creative_name}
                    </Link>
                  </td>
                )}
                <td className="aspectly:px-5 aspectly:py-3 aspectly:text-on-surface-variant aspectly:font-mono aspectly:text-xs">
                  {deepLink ? (
                    <Link
                      to={deepLink}
                      className="aspectly:text-primary aspectly:hover:underline aspectly:font-bold"
                    >
                      #{r.id}
                    </Link>
                  ) : (
                    `#${r.id}`
                  )}
                </td>
                <td
                  className={`aspectly:px-5 aspectly:py-3 aspectly:uppercase aspectly:tracking-wide aspectly:text-xs ${statusClass(r.status)}`}
                >
                  {r.status}
                </td>
                <td className="aspectly:px-5 aspectly:py-3 aspectly:text-on-surface-variant aspectly:font-medium">
                  {r.output_resolution}
                </td>
                <td className="aspectly:px-5 aspectly:py-3 aspectly:text-on-surface-variant">
                  {r.completed_at ? formatRelativeDate(r.completed_at) : '—'}
                </td>
                <td className="aspectly:px-5 aspectly:py-3 aspectly:text-on-surface-variant">
                  {formatDurationMs(r.render_duration_ms)}
                </td>
                <td className="aspectly:px-5 aspectly:py-3 aspectly:text-right aspectly:border-l aspectly:border-outline-variant/5">
                  {r.status === 'completed' && r.output_uri ? (
                    <button
                      type="button"
                      onClick={() =>
                        void downloadRenderContentFile(
                          r.creative_id,
                          r.id,
                          `${r.creative_name}-render-${r.id}.mp4`,
                        )
                      }
                      className="aspectly:text-primary aspectly:hover:text-primary-dim aspectly:font-bold aspectly:text-xs aspectly:uppercase aspectly:tracking-wide aspectly:cursor-pointer"
                    >
                      Download
                    </button>
                  ) : (
                    <span className="aspectly:text-on-surface-variant/40">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
